import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import B2BApiService from "../../B2bApiService";
import Loading from '../../components/reusableElementsComponents/Loading';
import WriteAccessGuard from '../../components/reusableElementsComponents/WriteAccessGuard';
import Table from 'react-bootstrap/Table';
import Button from '../../components/reusableElementsComponents/Button';
import { ToastContainer, toast } from "react-toastify";

import TextField from '@mui/material/TextField';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { t } from '../../content';

const CREDITS_TOAST = "creditsConsumptionToast";

const formatDate = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString();
};

const SORT_ACCESSORS = {
  first_name: (credit) => (credit.first_name || '').toLowerCase(),
  last_name: (credit) => (credit.last_name || '').toLowerCase(),
  email: (credit) => (credit.email || '').toLowerCase(),
  first_created: (credit) => {
    const time = new Date(credit.first_created).getTime();
    return isNaN(time) ? 0 : time;
  },
};

export default function CreditsConsumption({ companyId }) {
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('first_created');
  const [sortDir, setSortDir] = useState('desc');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const b2bServiceObj = useMemo(() => {
    return new B2BApiService();
  }, []);

  const loadCredits = useCallback((toastId) => {
    b2bServiceObj.GetCreditConsumption(
      companyId,
      (data) => {
        const allCredits = data?.all_credits || [];
        setCredits(allCredits);
        setLoading(false);
        toast.update(toastId, {
          render: allCredits.length === 1
            ? t('iam.toasts.creditsLoaded', { count: allCredits.length })
            : t('iam.toasts.creditsLoadedPlural', { count: allCredits.length }),
          type: "success",
          isLoading: false,
          autoClose: 2000,
          closeButton: true,
          hideProgressBar: true,
          containerId: CREDITS_TOAST,
        });
      },
      () => {
        setCredits([]);
        setLoading(false);
        toast.update(toastId, {
          render: t('iam.toasts.loadCreditsError'),
          type: "error",
          isLoading: false,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: true,
          containerId: CREDITS_TOAST,
        });
      }
    );
  }, [b2bServiceObj, companyId]);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const loadingToast = toast.loading(t('iam.toasts.loadingCredits'), { containerId: CREDITS_TOAST });
    loadCredits(loadingToast);
  }, [companyId, loadCredits]);

  const refreshCredits = () => {
    if (!companyId) return;
    const loadingToast = toast.loading(t('iam.toasts.updatingCredits'), { containerId: CREDITS_TOAST });
    b2bServiceObj.UpdateCreditConsumption(
      companyId,
      () => {
        loadCredits(loadingToast);
      },
      () => {
        toast.update(loadingToast, {
          render: t('iam.toasts.updateCreditsError'),
          type: "error",
          isLoading: false,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: true,
          containerId: CREDITS_TOAST,
        });
      }
    );
  };

  const filteredCredits = useMemo(() => {
    if (!startDate && !endDate) return credits;
    const start = startDate ? startDate.startOf('day') : null;
    const end = endDate ? endDate.endOf('day') : null;
    return credits.filter((credit) => {
      if (!credit.first_created) return false;
      const consumedDate = dayjs(credit.first_created);
      if (!consumedDate.isValid()) return false;
      if (start && consumedDate.isBefore(start)) return false;
      if (end && consumedDate.isAfter(end)) return false;
      return true;
    });
  }, [credits, startDate, endDate]);

  const sortedCredits = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return filteredCredits;
    return [...filteredCredits].sort((a, b) => {
      const valA = accessor(a);
      const valB = accessor(b);
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCredits, sortKey, sortDir]);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const clearPeriod = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const sortIndicator = (key) => {
    if (key !== sortKey) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const isPeriodActive = Boolean(startDate || endDate);

  const columns = [
    { key: 'first_name', label: t('common.fields.firstName') },
    { key: 'last_name', label: t('common.fields.lastName') },
    { key: 'email', label: t('common.fields.email') },
    { key: 'first_created', label: t('iam.creditConsumedDate') },
  ];

  if (loading) {
    return (
      <>
        <ToastContainer containerId={CREDITS_TOAST} />
        <Loading />
      </>
    );
  }

  return (
    <div className="settings-page">
      <ToastContainer containerId={CREDITS_TOAST} />
      <div className="settings-page-wrapper">
        <div className="settings" style={{ width: '100%' }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <strong className="m-2">{t('iam.creditsConsumption')}</strong>
            <div className="d-flex align-items-center flex-wrap">
              <span className="m-2 grey">
                Total credits consumed: <strong>{filteredCredits.length}</strong>
                {isPeriodActive && <> of {credits.length}</>}
              </span>
              <WriteAccessGuard>
                <Button type='button' btnstyle={'mazhrBtn default-green'} onClick={refreshCredits} disabled={loading}>
                  {t('iam.refresh')}
                </Button>
              </WriteAccessGuard>
            </div>
          </div>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="d-flex align-items-center flex-wrap m-2" style={{ gap: '12px' }}>
              <DatePicker
                label={t('common.labels.from')}
                value={startDate}
                maxDate={endDate || undefined}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
              <DatePicker
                label={t('common.labels.to')}
                value={endDate}
                minDate={startDate || undefined}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
              {isPeriodActive &&
                <Button type='button' btnstyle={'mazhrBtn light-grey'} onClick={clearPeriod}>
                  Clear
                </Button>
              }
            </div>
          </LocalizationProvider>

          <hr style={{ margin: '10px 0px 20px 0px' }} />

          {sortedCredits.length === 0 ? (
            <p className="m-2">
              {isPeriodActive
                ? t('iam.noCreditsPeriod')
                : t('iam.noCreditRecords')}
            </p>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      onClick={() => handleSort(column.key)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      {column.label}{sortIndicator(column.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedCredits.map((credit) => (
                  <tr key={credit.id}>
                    <td>{credit.first_name || '-'}</td>
                    <td>{credit.last_name || '-'}</td>
                    <td>{credit.email || '-'}</td>
                    <td>{formatDate(credit.first_created)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
